import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Invoice, InvoiceProjectCustomer, ItemizedInvoice } from './invoiceType';
import InvoiceApi from './invoiceApi';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Save, Trash2, Plus, Package } from 'lucide-react';
import { formatCurrency } from '../lib/global_function';

const UpdateInvoicePage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Invoice', path: '/main/project/invoice' },
    { label: 'Update', path: '/main/project/invoice/update' },
  ];

  const location = useLocation();
  const navigate = useNavigate();
  const invoice: InvoiceProjectCustomer | undefined = location.state?.invoice;

  // Helper to extract date in YYYY-MM-DD format for date inputs
  const extractDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [itemizedInvoices, setItemizedInvoices] = useState<ItemizedInvoice[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [calculatedTotals, setCalculatedTotals] = useState({
    subtotal: 0,
    taxAmount: 0,
    finalTotal: 0,
    balanceDue: 0
  });

  const [invoiceEntered, setInvoiceEntered] = useState({
    name: invoice?.name || '',
    payment_instruction: invoice?.payment_instruction || '',
    discount_usd: invoice?.discount_usd || 0,
    tax_rate: invoice?.tax_rate || 0,
    due_date: extractDateForInput(invoice?.due_date),
    issue_date: extractDateForInput(invoice?.issue_date),
    amount_paid: invoice?.amount_paid || 0,
    payment_method: invoice?.payment_method || '',
    payment_date: extractDateForInput(invoice?.payment_date),
    note: invoice?.note || '',
  });

  // Payment method options
  const paymentMethods = [
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'cash', label: 'Cash' },
    { value: 'check', label: 'Check' },
    { value: 'money_order', label: 'Money Order' },
  ];

  // Check if invoice is available
  useEffect(() => {
    if (!invoice) {
      setErrorMessage('Invoice not available, please select an invoice by clicking Go To Invoice button.');
    }
  }, [invoice]);

  // Fetch all invoice data using combined endpoint
  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!invoice) return;

      try {
        const result = await InvoiceApi.getInvoiceById(invoice.invoice_id);

        // Update invoice form - access properties from result.invoice
        setInvoiceEntered({
          name: result.invoice.name,
          payment_instruction: result.invoice.payment_instruction,
          discount_usd: result.invoice.discount_usd,
          tax_rate: result.invoice.tax_rate,
          due_date: extractDateForInput(result.invoice.due_date),
          issue_date: extractDateForInput(result.invoice.issue_date),
          amount_paid: result.totals.amount_paid,
          payment_method: result.invoice.payment_method || '',
          payment_date: extractDateForInput(result.invoice.payment_date),
          note: result.invoice.note || '',
        });

        // Update line items - access from result.itemizedInvoices
        setItemizedInvoices(result.itemizedInvoices);

        // Initial totals (will recalculate via useEffect) - access from result.totals
        setCalculatedTotals({
          subtotal: result.totals.sub_total,
          taxAmount: result.totals.tax_amount,
          finalTotal: result.totals.sub_total + result.totals.tax_amount - result.totals.discount_usd,
          balanceDue: result.totals.balance
        });
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    };
    fetchInvoiceData();
  }, [invoice]);

  // Dynamic calculation of totals
  useEffect(() => {
    const calculateTotals = () => {
      const subtotal = itemizedInvoices.reduce((sum, item) => {
        const amount = Number(item.amount_dollar) || 0;
        return sum + amount;
      }, 0);

      const taxRate = Number(invoiceEntered.tax_rate) || 0;
      const discountUsd = Number(invoiceEntered.discount_usd) || 0;
      const amountPaid = Number(invoiceEntered.amount_paid) || 0;

      const taxAmount = (subtotal * taxRate) / 100;
      const finalTotal = subtotal + taxAmount - discountUsd;
      const balanceDue = finalTotal - amountPaid;

      setCalculatedTotals({
        subtotal,
        taxAmount,
        finalTotal,
        balanceDue
      });
    };

    calculateTotals();
  }, [itemizedInvoices, invoiceEntered.tax_rate, invoiceEntered.discount_usd, invoiceEntered.amount_paid]);

  const handleItemizedChange = (id: string, field: keyof ItemizedInvoice, value: any) => {
    setItemizedInvoices((prev) =>
      prev.map((item) =>
        String(item.itemized_invoice_id) === String(id) ? { ...item, [field]: value } : item
      )
    );
  };

  const refreshInvoiceData = async () => {
    if (!invoice) return;

    try {
      const result = await InvoiceApi.getInvoiceById(invoice.invoice_id);

      setInvoiceEntered({
        name: result.invoice.name,
        payment_instruction: result.invoice.payment_instruction,
        discount_usd: result.invoice.discount_usd,
        tax_rate: result.invoice.tax_rate,
        due_date: extractDateForInput(result.invoice.due_date),
        issue_date: extractDateForInput(result.invoice.issue_date),
        amount_paid: result.invoice.amount_paid,
        payment_method: result.invoice.payment_method || '',
        payment_date: extractDateForInput(result.invoice.payment_date),
        note: result.invoice.note || '',
      });

      setItemizedInvoices(result.itemizedInvoices);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  const handleDeleteItemizedInvoice = async (id: string) => {
    if (!invoice) return;

    const item = itemizedInvoices.find((item) => String(item.itemized_invoice_id) === String(id));
    if (!item) return;

    setErrorMessage('');
    setMessage('');

    try {
      // Check if this is an unsaved item (temporary ID starts with "temp-")
      if (String(item.itemized_invoice_id).startsWith('temp-')) {
        setItemizedInvoices((prev) => prev.filter((i) => String(i.itemized_invoice_id) !== String(id)));
        setMessage('Unsaved line item removed.');
      } else {
        await InvoiceApi.deleteItemizedInvoice(String(id));
        setItemizedInvoices((prev) => prev.filter((i) => String(i.itemized_invoice_id) !== String(id)));
        setMessage('Line item deleted successfully.');
        await refreshInvoiceData();
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  const addNewLine = () => {
    if (!invoice) return;

    const newItem: ItemizedInvoice = {
      itemized_invoice_id: `temp-${Date.now()}-${Math.random()}`, // Temporary unique ID for unsaved items
      invoice_id: invoice.invoice_id,
      code: '',
      description: '',
      amount_dollar: 0,
    };
    setItemizedInvoices((prev) => [...prev, newItem]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice) return;

    setErrorMessage('');
    setMessage('');

    if (!invoiceEntered.name.trim()) {
      setErrorMessage('Invoice name is required');
      return;
    }

    // Validate line items
    if (itemizedInvoices.length > 0) {
      for (let i = 0; i < itemizedInvoices.length; i++) {
        const item = itemizedInvoices[i];
        if (!item.code.trim()) {
          setErrorMessage(`Line item ${i + 1}: Code is required`);
          return;
        }
        if (!item.description.trim()) {
          setErrorMessage(`Line item ${i + 1}: Description is required`);
          return;
        }
        if (!item.amount_dollar || item.amount_dollar <= 0) {
          setErrorMessage(`Line item ${i + 1}: Amount must be greater than 0`);
          return;
        }
      }
    }

    // Conditional validation for payment fields
    if (invoiceEntered.amount_paid > 0) {
      if (!invoiceEntered.payment_method) {
        setErrorMessage('Please select a payment method when amount paid is greater than 0.');
        return;
      }
      if (!invoiceEntered.payment_date) {
        setErrorMessage('Please enter a payment date when amount paid is greater than 0.');
        return;
      }
    }

    try {
      // Prepare invoice data
      const invoiceData: Partial<Invoice> = {
        name: invoiceEntered.name,
        payment_instruction: invoiceEntered.payment_instruction,
        tax_rate: invoiceEntered.tax_rate || 0,
        discount_usd: invoiceEntered.discount_usd || 0,
        issue_date: invoiceEntered.issue_date || '',
        due_date: invoiceEntered.due_date || '',
        amount_paid: invoiceEntered.amount_paid || 0,
        payment_method: invoiceEntered.payment_method || '',
        payment_date: invoiceEntered.payment_date || '',
        note: invoiceEntered.note || '',
      };

      // Transform line items for API
      // Items with temp- IDs are new (no id field)
      // Items without temp- IDs are existing (include id field)
      const lineItems = itemizedInvoices.map(item => {
        const baseItem = {
          code: item.code,
          description: item.description,
          amount_dollar: item.amount_dollar,
        };

        // Only include id for existing items (not temp- items)
        if (!String(item.itemized_invoice_id).startsWith('temp-')) {
          return {
            ...baseItem,
            id: typeof item.itemized_invoice_id === 'number'
              ? item.itemized_invoice_id
              : parseInt(item.itemized_invoice_id),
          };
        }

        return baseItem;
      });

      // Single atomic API call to update invoice and all line items
      await InvoiceApi.updateInvoiceAndLineitems(
        invoice.invoice_id,
        invoiceData,
        lineItems
      );

      setMessage('Invoice and line items saved successfully');

      await refreshInvoiceData();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  const handleNavigateToInvoiceList = () => {
    navigate('/main/project/invoice');
  };

  const taxAmount = Number(calculatedTotals.taxAmount) || 0;
  const discountAmount = Number(invoiceEntered.discount_usd) || 0;

  return (
    <>
      <HorizontalMenu />
      <div className="px-4 py-6 max-w-7xl mx-auto">
        <Breadcrumbs items={breadcrumbs} />

        <h1 className="text-3xl font-bold text-foreground mt-6 mb-6">Update Invoice</h1>

        {errorMessage && (
          <Alert variant="error" className="mb-6">
            <p>{errorMessage}</p>
            {!invoice && (
              <Button onClick={handleNavigateToInvoiceList} className="mt-3">
                Go to Invoice
              </Button>
            )}
          </Alert>
        )}

        {message && (
          <Alert variant="success" className="mb-6">
            {message}
          </Alert>
        )}

        {invoice ? (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Form Section - 2 columns */}
              <div className="lg:col-span-2 space-y-6">
                {/* Invoice Details Section */}
                <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Invoice Details</h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Invoice Name <span className="text-destructive">*</span>
                      </label>
                      <Input
                        value={invoiceEntered.name}
                        onChange={(e) => setInvoiceEntered({ ...invoiceEntered, name: e.target.value })}
                        required
                        placeholder="Enter invoice name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Payment Instructions
                      </label>
                      <Textarea
                        value={invoiceEntered.payment_instruction}
                        onChange={(e) => setInvoiceEntered({ ...invoiceEntered, payment_instruction: e.target.value })}
                        placeholder="Enter payment instructions"
                        className="min-h-24"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Tax Rate (%)</label>
                        <Input
                          type="number"
                          min="0"
                          max="50"
                          step="0.1"
                          placeholder="0"
                          value={invoiceEntered.tax_rate === 0 ? '' : invoiceEntered.tax_rate}
                          onChange={(e) => {
                            const inputValue = parseFloat(e.target.value) || 0;
                            const cappedValue = Math.min(inputValue, 50);
                            setInvoiceEntered({ ...invoiceEntered, tax_rate: cappedValue });
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Discount ($)</label>
                        <Input
                          type="number"
                          min="0"
                          max="10000"
                          step="10"
                          placeholder="0"
                          value={invoiceEntered.discount_usd === 0 ? '' : invoiceEntered.discount_usd}
                          onChange={(e) => {
                            const inputValue = parseFloat(e.target.value) || 0;
                            const cappedValue = Math.min(inputValue, 10000);
                            setInvoiceEntered({ ...invoiceEntered, discount_usd: cappedValue });
                          }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Issue Date <span className="text-destructive">*</span>
                        </label>
                        <Input
                          type="date"
                          value={invoiceEntered.issue_date || ''}
                          onChange={(e) => setInvoiceEntered({ ...invoiceEntered, issue_date: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Due Date <span className="text-destructive">*</span>
                        </label>
                        <Input
                          type="date"
                          value={invoiceEntered.due_date || ''}
                          onChange={(e) => setInvoiceEntered({ ...invoiceEntered, due_date: e.target.value })}
                          required
                        />
                      </div>
                    </div>

{/* Payment Received Section
                    <div className="p-4 bg-gray-50 border border-gray-300 rounded-lg space-y-4">
                      <h3 className="text-sm font-semibold text-foreground">Payment Received</h3>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Total Amount Received ($)
                          </label>
                          <Input
                            type="number"
                            min="0"
                            max="100000"
                            placeholder="0"
                            value={invoiceEntered.amount_paid === 0 ? '' : invoiceEntered.amount_paid}
                            onChange={(e) => {
                              const inputValue = parseFloat(e.target.value) || 0;
                              const cappedValue = Math.min(inputValue, 100000);
                              setInvoiceEntered({ ...invoiceEntered, amount_paid: cappedValue });
                            }}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Payment Method {invoiceEntered.amount_paid > 0 && <span className="text-destructive">*</span>}
                          </label>
                          <Select
                            value={invoiceEntered.payment_method}
                            onChange={(e) => setInvoiceEntered({ ...invoiceEntered, payment_method: e.target.value })}
                            required={invoiceEntered.amount_paid > 0}
                          >
                            <option value="">Select Method</option>
                            {paymentMethods.map((method) => (
                              <option key={method.value} value={method.value}>
                                {method.label}
                              </option>
                            ))}
                          </Select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Payment Date {invoiceEntered.amount_paid > 0 && <span className="text-destructive">*</span>}
                          </label>
                          <Input
                            type="date"
                            value={invoiceEntered.payment_date}
                            onChange={(e) => setInvoiceEntered({ ...invoiceEntered, payment_date: e.target.value })}
                            required={invoiceEntered.amount_paid > 0}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Note</label>
                        <Textarea
                          value={invoiceEntered.note}
                          onChange={(e) => setInvoiceEntered({ ...invoiceEntered, note: e.target.value })}
                          placeholder="Enter Note"
                          rows={2}
                          className="resize-none"
                        />
                      </div>
                    </div>

 */}

                  </div>
                </div>

                {/* Line Items Section */}
                <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-foreground">Line Items</h2>
                    <Button type="button" onClick={addNewLine} size="medium" variant="inverted">
                      <Plus className="size-5" />
                      Add Line Item
                    </Button>
                  </div>

                  {itemizedInvoices.length === 0 ? (
                    <div className="text-center py-12 text-paragraph">
                      <Package className="size-12 mx-auto mb-3 text-muted" />
                      <p>No line items yet. Click "Add Line Item" to get started.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {itemizedInvoices.map((item, index) => (
                        <div
                          key={String(item.itemized_invoice_id)}
                          className="border-2 border-muted rounded-lg p-4 hover:border-primary/20 transition-colors"
                        >
                          {/* Single Row: Code, Description, Amount, Delete */}
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Code <span className="text-destructive">*</span>
                              </label>
                              <Input
                                placeholder="Code"
                                value={item.code}
                                onChange={(e) =>
                                  handleItemizedChange(String(item.itemized_invoice_id), 'code', e.target.value)
                                }
                                className="text-sm"
                              />
                            </div>

                            <div className="md:col-span-5">
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Description <span className="text-destructive">*</span>
                              </label>
                              <Input
                                placeholder="Enter description"
                                value={item.description}
                                onChange={(e) =>
                                  handleItemizedChange(String(item.itemized_invoice_id), 'description', e.target.value)
                                }
                                className="text-sm"
                              />
                            </div>

                            <div className="md:col-span-3">
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Amount ($) <span className="text-destructive">*</span>
                              </label>
                              <Input
                                type="number"
                                placeholder="0.00"
                                value={item.amount_dollar === 0 ? '' : item.amount_dollar}
                                onChange={(e) => {
                                  const inputValue = parseFloat(e.target.value) || 0;
                                  handleItemizedChange(String(item.itemized_invoice_id), 'amount_dollar', inputValue);
                                }}
                                min="0"
                                step="0.01"
                                className="text-sm"
                              />
                            </div>

                            <div className="md:col-span-2 flex items-end">
                              <Button
                                type="button"
                                size="medium"
                                variant="inverted"
                                onClick={() => handleDeleteItemizedInvoice(String(item.itemized_invoice_id))}
                                className="w-full"
                              >
                                <Trash2 className="size-4" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Save Button */}
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    size="medium"
                    variant="inverted"
                    onClick={handleNavigateToInvoiceList}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" size="medium">
                    <Save className="size-5" />
                    Save All Changes
                  </Button>
                </div>
              </div>

              {/* Sticky Invoice Summary - 1 column */}
              <div className="lg:col-span-1">
                <div className="sticky top-6 p-6 bg-gradient-to-br from-primary/5 to-primary/10 border-2 border-primary/30 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Invoice Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Subtotal (Before Tax):</span>
                      <span className="font-semibold">${formatCurrency(Number(calculatedTotals.subtotal).toFixed(2))}</span>
                    </div>
                    {Number(invoiceEntered.tax_rate ?? 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Tax ({invoiceEntered.tax_rate}%):</span>
                        <span className="font-semibold">${formatCurrency(taxAmount.toFixed(2))}</span>
                      </div>
                    )}
                    {Number(discountAmount ?? 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Discount:</span>
                        <span className="font-semibold text-red-600">-${formatCurrency(discountAmount.toFixed(2))}</span>
                      </div>
                    )}
                    <div className="pt-3 border-t-2 border-primary/30">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-foreground">Total Amount:</span>
                        <span className="text-2xl font-bold text-primary">
                          ${formatCurrency(Number(calculatedTotals.finalTotal).toFixed(2))}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-300">
                      <span className="text-gray-700">Amount Paid:</span>
                      <span className="font-semibold text-green-600">
                        ${formatCurrency(Number(invoiceEntered.amount_paid).toFixed(2))}
                      </span>
                    </div>
                    <div className="pt-2 border-t-2 border-primary/30">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-foreground">Balance Due:</span>
                        <span className="text-2xl font-bold text-primary">
                          ${formatCurrency(Number(calculatedTotals.balanceDue).toFixed(2))}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        ) : (
          <div className="bg-white rounded-2xl border-2 border-muted p-12 text-center">
            <p className="text-paragraph">No invoice data available to display.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default UpdateInvoicePage;